import type { BossId, ChapterView, LevelView, SoloAbility } from '@bioforge/protocol';
import { Container, FillGradient, Graphics, Text, type Ticker } from 'pixi.js';
import {
  ABILITY_NAMES,
  BOSS_NAMES,
  CHAPTER_PLACES,
  SOLO_ABILITY_HINTS,
  VESSEL_ACCUSATIVE,
  VESSEL_NAMES,
  loadout,
} from './labels.js';
import { nextLevel } from './menu-scene.js';
import { listen, type AppContext, type Navigator, type Scene } from './scene.js';
import { tr } from './story/story.js';
import { DesignFrame, currentFrame, screenBackground } from './ui/frame.js';
import { idle, motionReduced } from './ui/motion.js';
import { artSprite } from './ui/textures.js';
import { COLOR, textStyle } from './ui/theme.js';
import { UiButton, panel, vertical } from './ui/widgets.js';
import { dashes, samplePath, smoothPath, type Point } from './view/path.js';

/** Узлы обычных уровней на пути и вход узла босса (координаты макета внутри блока уровней). */
const NODES: readonly Point[] = [
  { x: 80, y: 62 },
  { x: 300, y: 122 },
  { x: 520, y: 62 },
  { x: 740, y: 122 },
];
const BOSS_ENTRY: Point = { x: 1060, y: 92 };
/** Карточка босса в блоке уровней. */
const BOSS_CARD: Point = { x: 1040, y: 26 };

/** Телефон: узлы уровней зигзагом, карточка босса под ними. */
const NODES_TALL: readonly Point[] = [
  { x: 70, y: 60 },
  { x: 230, y: 112 },
  { x: 390, y: 60 },
  { x: 550, y: 112 },
];
const BOSS_CARD_TALL: Point = { x: 380, y: 150 };
const BOSS_ENTRY_TALL: Point = { x: 400, y: 216 };

/** Ширина содержимого: макет и телефон. */
const WIDE_W = 1320;
const TALL_W = 676;

const caps = (text: string, color: number = COLOR.mint, letterSpacing = 1.5) =>
  new Text({
    text: text.toUpperCase(),
    style: textStyle({ size: 11, display: true, weight: '800', color, letterSpacing }),
  });

const formatScore = (score: number) => score.toLocaleString('ru-RU');

/** Экспедиция по макету «Экспедиция · главы и уровни»: главы, путь уровней, выбранный уровень. */
export class ExpeditionScene implements Scene {
  readonly view: DesignFrame;
  /** Подписки экрана (клавиатура). */
  private readonly listeners: (() => void)[] = [];
  /** Анимации текущей раскладки — останавливаются при перерисовке. */
  private readonly stops: (() => void)[] = [];
  private readonly chapters = new Container();
  private readonly levels = new Container();
  private readonly details = new Container();
  private readonly notice: Text;
  private chapter: ChapterView;
  private level: LevelView;
  private flowOffset = 0;
  private readonly flow = new Graphics();
  /** Телефон в портрете (SPEC.md, 16.3). */
  private readonly tall: boolean;
  private readonly nodes: readonly Point[];
  private readonly bossCard: Point;
  private readonly path: Point[];
  private readonly onTick = (ticker: Ticker): void => {
    this.flowOffset += ticker.deltaMS * 0.047;
    this.drawFlow();
  };

  constructor(
    private readonly nav: Navigator,
    private readonly ctx: AppContext,
    private readonly ticker: Ticker,
    notice = '',
  ) {
    const frame = currentFrame();
    this.tall = frame.width < frame.height;
    this.view = new DesignFrame(screenBackground(0.5, 0.4, [], frame), frame);
    this.nodes = this.tall ? NODES_TALL : NODES;
    this.bossCard = this.tall ? BOSS_CARD_TALL : BOSS_CARD;
    this.path = samplePath(smoothPath([...this.nodes, this.tall ? BOSS_ENTRY_TALL : BOSS_ENTRY]));
    const all = ctx.content.chapters.flatMap((c) => c.levels);
    const start = nextLevel(ctx.content, ctx.progress) ?? all.at(-1) ?? null;
    const first = ctx.content.chapters[0];
    if (!start || !first) throw new Error('Экспедиция без уровней');
    this.level = start;
    this.chapter = ctx.content.chapters.find((c) => c.number === start.chapter) ?? first;

    this.header();
    this.notice = new Text({
      text: notice,
      style: textStyle({ size: 15, color: 0xffb3c8, weight: '500' }),
    });
    const side = this.tall ? 22 : 60;
    this.notice.position.set(side, this.tall ? 1246 : 78);
    this.chapters.position.set(side, this.tall ? 116 : 104);
    this.levels.position.set(side, this.tall ? 250 : 376);
    this.details.position.set(side, this.tall ? 576 : 604);
    this.view.content.addChild(this.chapters, this.levels, this.details, this.notice);
    this.render();

    if (!motionReduced()) ticker.add(this.onTick);
    this.listeners.push(
      listen('keydown', (event) => {
        if (event.code === 'Escape') nav.menu();
        if (event.code === 'Enter') this.start();
      }),
    );
  }

  private header(): void {
    const root = this.view.content;
    const back = new UiButton({
      width: 104,
      height: 44,
      radius: 12,
      label: 'Меню',
      fontSize: 15,
      center: true,
      icon: { key: 'icon-back', size: 18 },
      onTap: () => this.nav.menu(),
    });
    const side = this.tall ? 22 : 60;
    back.position.set(side, 22);
    const title = new Text({
      text: 'Экспедиция',
      style: textStyle({ size: 30, display: true, weight: '900', shadow: 3 }),
    });
    title.anchor.set(0, 0.5);
    title.position.set(side + 104 + 18, 44);

    const total = this.ctx.content.chapters.reduce((n, c) => n + c.levels.length, 0);
    const done = Object.keys(this.ctx.progress.levels).length;
    const bar = new Graphics()
      .roundRect(0, 0, 160, 10, 5)
      .fill({ color: COLOR.mint, alpha: 0.12 })
      .roundRect(0, 0, (160 * done) / Math.max(1, total), 10, 5)
      .fill(COLOR.teal);
    bar.position.set(this.view.size.width - side - 160, this.tall ? 84 : 39);
    const passed = new Text({
      text: `Пройдено ${done} из ${total} уровней`,
      style: textStyle({ size: 15, color: COLOR.textSoft }),
    });
    passed.anchor.set(1, 0.5);
    passed.position.set(bar.x - 14, this.tall ? 89 : 44);
    root.addChild(back, title, passed, bar);
  }

  private render(): void {
    this.stops.splice(0).forEach((stop) => stop());
    for (const box of [this.chapters, this.levels, this.details]) {
      box.removeChildren().forEach((child) => child.destroy({ children: true }));
    }
    this.renderChapters();
    this.renderLevels();
    this.renderDetails();
  }

  private chapterOpen(chapter: ChapterView): boolean {
    const first = chapter.levels[0];
    return first !== undefined && this.ctx.progress.unlocked.includes(first.id);
  }

  private renderChapters(): void {
    if (this.tall) {
      this.renderChaptersTall();
      return;
    }
    const width = (1320 - 4 * 16) / 5;
    const chapters = this.ctx.content.chapters;
    chapters.forEach((chapter, i) => {
      const open = this.chapterOpen(chapter);
      const current = chapter.number === this.chapter.number;
      const done = chapter.levels.filter((l) => this.ctx.progress.levels[l.id]).length;
      const card = new Container();
      card.position.set(i * (width + 16), 0);
      const bg = new Graphics();
      if (current) {
        bg.roundRect(-3, -3, width + 6, 266, 22).fill({ color: COLOR.mint, alpha: 0.12 });
        bg.roundRect(0, 0, width, 260, 20)
          .fill(vertical(0x245678, 0x0e1c3a))
          .stroke({ width: 3, color: COLOR.mint });
      } else {
        bg.roundRect(0, 0, width, 260, 20)
          .fill({ color: 0x0e1c3a, alpha: 0.7 })
          .stroke({ width: 2, color: COLOR.borderDim });
      }
      const number = caps(`Глава ${chapter.number}`, current ? COLOR.mint : 0x8fa9bc);
      number.position.set(16, 16);
      card.addChild(bg, number);
      if (!open) {
        const lock = artSprite('icon-lock', 20);
        lock.position.set(width - 26, 26);
        card.addChild(lock);
      }
      const vessel = artSprite(`icon-vessel-${chapter.vessel}`, 84);
      vessel.position.set(width / 2, 86);
      const name = new Text({
        text: VESSEL_NAMES[chapter.vessel],
        style: textStyle({ size: 18, display: true, weight: '800' }),
      });
      name.position.set(16, 136);
      const previous = chapters[i - 1];
      const note = new Text({
        text:
          open || !previous
            ? `${CHAPTER_PLACES[chapter.vessel]} · босс ${BOSS_NAMES[chapter.bossId]}`
            : `Победи ${bossAccusative(previous.bossId)}, чтобы открыть`,
        style: textStyle({ size: 13, color: COLOR.textSoft, wrap: width - 32, lineHeight: 17 }),
      });
      note.position.set(16, 166);
      const track = new Graphics()
        .roundRect(16, 224, width - 32 - 40, 8, 4)
        .fill({ color: COLOR.mint, alpha: 0.12 })
        .roundRect(16, 224, ((width - 72) * done) / chapter.levels.length, 8, 4)
        .fill(COLOR.teal);
      const count = new Text({
        text: `${done}/${chapter.levels.length}`,
        style: textStyle({ size: 12, display: true, weight: '800' }),
      });
      count.anchor.set(1, 0.5);
      count.position.set(width - 16, 228);
      card.addChild(vessel, name, note, track, count);
      card.alpha = current ? 1 : open ? 0.9 : 0.55;
      card.eventMode = 'static';
      card.cursor = open ? 'pointer' : 'not-allowed';
      card.accessible = true;
      card.accessibleType = 'button';
      card.accessibleTitle = `Глава ${chapter.number}: ${VESSEL_NAMES[chapter.vessel]}${open ? '' : ', закрыта'}`;
      card.on('pointertap', () => {
        if (!open) {
          this.notice.text = 'Глава закрыта: победите босса предыдущей главы.';
          return;
        }
        this.notice.text = '';
        this.chapter = chapter;
        const unlocked = chapter.levels.filter((l) => this.ctx.progress.unlocked.includes(l.id));
        this.level =
          unlocked.find((l) => !this.ctx.progress.levels[l.id]) ?? unlocked.at(-1) ?? this.level;
        this.render();
      });
      this.chapters.addChild(card);
    });
  }

  /** Телефон: главы — компактные карточки в ряд (сосуд, номер, прогресс). */
  private renderChaptersTall(): void {
    const gap = 10;
    const width = (TALL_W - 4 * gap) / 5;
    const height = 120;
    this.ctx.content.chapters.forEach((chapter, i) => {
      const open = this.chapterOpen(chapter);
      const current = chapter.number === this.chapter.number;
      const done = chapter.levels.filter((l) => this.ctx.progress.levels[l.id]).length;
      const card = new Container();
      card.position.set(i * (width + gap), 0);
      const bg = new Graphics();
      if (current) {
        bg.roundRect(0, 0, width, height, 18)
          .fill(vertical(0x245678, 0x0e1c3a))
          .stroke({ width: 3, color: COLOR.mint });
      } else {
        bg.roundRect(0, 0, width, height, 18)
          .fill({ color: 0x0e1c3a, alpha: 0.7 })
          .stroke({ width: 2, color: COLOR.borderDim });
      }
      const number = caps(`Глава ${chapter.number}`, current ? COLOR.mint : 0x8fa9bc, 0.5);
      number.anchor.set(0.5, 0);
      number.position.set(width / 2, 12);
      const vessel = artSprite(`icon-vessel-${chapter.vessel}`, 52);
      vessel.position.set(width / 2, 60);
      const count = new Text({
        text: open ? `${done}/${chapter.levels.length}` : '',
        style: textStyle({ size: 13, display: true, weight: '800' }),
      });
      count.anchor.set(0.5, 0);
      count.position.set(width / 2, 92);
      card.addChild(bg, number, vessel, count);
      if (!open) {
        const lock = artSprite('icon-lock', 22);
        lock.position.set(width / 2, 102);
        card.addChild(lock);
      }
      card.alpha = current ? 1 : open ? 0.9 : 0.55;
      this.chapterTap(card, chapter, open, i);
      this.chapters.addChild(card);
    });
  }

  /** Касание главы: открытая — показать её уровни, закрытая — подсказка, кого победить. */
  private chapterTap(card: Container, chapter: ChapterView, open: boolean, index: number): void {
    const previous = this.ctx.content.chapters[index - 1];
    card.eventMode = 'static';
    card.cursor = open ? 'pointer' : 'not-allowed';
    card.accessible = true;
    card.accessibleType = 'button';
    card.accessibleTitle = `Глава ${chapter.number}: ${VESSEL_NAMES[chapter.vessel]}${open ? '' : ', закрыта'}`;
    card.on('pointertap', () => {
      if (!open) {
        this.notice.text = previous
          ? `Глава закрыта: победите ${bossAccusative(previous.bossId)}.`
          : 'Глава закрыта.';
        return;
      }
      this.notice.text = '';
      this.chapter = chapter;
      const unlocked = chapter.levels.filter((l) => this.ctx.progress.unlocked.includes(l.id));
      this.level =
        unlocked.find((l) => !this.ctx.progress.levels[l.id]) ?? unlocked.at(-1) ?? this.level;
      this.render();
    });
  }

  private renderLevels(): void {
    const chapter = this.chapter;
    const heading = new Text({
      text: `Глава ${chapter.number} · ${VESSEL_NAMES[chapter.vessel]}`,
      style: textStyle({ size: 20, display: true, weight: '800', shadow: 2 }),
    });
    const place = new Text({
      text: `Отсек: ${CHAPTER_PLACES[chapter.vessel].toLowerCase()}`,
      style: textStyle({ size: 14, color: COLOR.textMuted }),
    });
    place.anchor.set(1, 0);
    place.position.set(this.tall ? TALL_W : WIDE_W, 4);
    this.levels.addChild(heading, place);

    const track = new Container();
    track.position.set(0, 30);
    const road = new Graphics();
    const line = (width: number, color: number, alpha = 1, dy = 0) => {
      const [first, ...rest] = this.path;
      if (!first) return;
      road.moveTo(first.x, first.y + dy);
      for (const p of rest) road.lineTo(p.x, p.y + dy);
      road.stroke({ width, color, alpha, cap: 'round', join: 'round' });
    };
    line(28, COLOR.ink);
    line(20, 0x1e4660);
    line(3, COLOR.white, 0.18, -7);
    track.addChild(road, this.flow);
    this.drawFlow();

    const normal = chapter.levels.filter((l) => l.kind === 'normal');
    normal.forEach((level, i) => {
      const node = this.nodes[i];
      if (node) track.addChild(this.levelNode(level, node, i));
    });
    const boss = chapter.levels.find((l) => l.kind === 'boss');
    if (boss) track.addChild(this.bossNode(boss));
    this.levels.addChild(track);
  }

  private drawFlow(): void {
    this.flow.clear();
    for (const [a, b] of dashes(this.path, 16, 10, this.flowOffset)) {
      this.flow.moveTo(a.x, a.y).lineTo(b.x, b.y);
    }
    this.flow.stroke({ width: 9, color: COLOR.teal, cap: 'round' });
  }

  private levelNode(level: LevelView, at: Point, index: number): Container {
    const open = this.ctx.progress.unlocked.includes(level.id);
    const result = this.ctx.progress.levels[level.id];
    const selected = level.id === this.level.id;
    const node = new Container();
    node.position.set(at.x, at.y);
    const body = new Container();
    const g = new Graphics();
    if (selected) g.circle(0, 0, 58).fill({ color: COLOR.gold, alpha: 0.18 });
    g.circle(0, 6, 46).fill(COLOR.ink);
    g.circle(0, 0, 46)
      .fill(
        new FillGradient({
          type: 'radial',
          center: { x: 0.35, y: 0.3 },
          innerRadius: 0,
          outerCenter: { x: 0.35, y: 0.3 },
          outerRadius: 0.8,
          colorStops: open
            ? [
                { offset: 0, color: 0xcffff8 },
                { offset: 0.36, color: COLOR.teal },
                { offset: 1, color: 0x128a82 },
              ]
            : [
                { offset: 0, color: 0x6a7f95 },
                { offset: 1, color: 0x26303f },
              ],
          textureSpace: 'local',
        }),
      )
      .stroke({ width: 4, color: selected ? COLOR.gold : COLOR.ink });
    g.ellipse(-16, -26, 14, 6.5).fill({ color: COLOR.white, alpha: open ? 0.6 : 0.25 });
    const number = new Text({
      text: String(level.index),
      style: textStyle({
        size: 36,
        display: true,
        weight: '900',
        color: open ? COLOR.white : 0x9fb3c4,
        shadow: 3,
      }),
    });
    number.anchor.set(0.5);
    body.addChild(g, number);
    if (result) {
      const badge = new Graphics()
        .circle(38, -38, 16)
        .fill(COLOR.gold)
        .stroke({ width: 3, color: COLOR.ink });
      const check = artSprite('icon-check', 16);
      check.position.set(38, -38);
      body.addChild(badge, check);
      const score = new Text({
        text: formatScore(result.bestScore),
        style: textStyle({ size: 11, display: true, weight: '800', color: COLOR.goldSoft }),
      });
      score.anchor.set(0.5);
      const pill = new Graphics()
        .roundRect(-score.width / 2 - 10, 46 - 2, score.width + 20, 22, 11)
        .fill(COLOR.ink)
        .stroke({ width: 2, color: COLOR.teal });
      score.position.set(0, 46 + 9);
      body.addChild(pill, score);
    } else if (!open) {
      const lock = artSprite('icon-lock', 20);
      lock.position.set(38, -38);
      body.addChild(lock);
    }
    node.addChild(body);
    if (open) this.stops.push(idle(body, 'float', index * 0.7));
    this.interactive(
      node,
      level,
      open,
      `Уровень ${level.index}${result ? `, пройден, лучший счёт ${formatScore(result.bestScore)}` : open ? '' : ', закрыт'}`,
    );
    return node;
  }

  private bossNode(level: LevelView): Container {
    const open = this.ctx.progress.unlocked.includes(level.id);
    const result = this.ctx.progress.levels[level.id];
    const selected = level.id === this.level.id;
    const bossId = level.bossId ?? this.chapter.bossId;
    const node = new Container();
    node.position.set(this.bossCard.x, this.bossCard.y);
    const glow = new Graphics()
      .roundRect(-6, -6, 292, 144, 30)
      .fill({ color: COLOR.pinkDark, alpha: selected ? 0.45 : 0.25 });
    const bg = new Graphics()
      .roundRect(0, 0, 280, 132, 26)
      .fill(vertical(0x5a1e46, 0x2a0c24))
      .stroke({ width: 3, color: selected ? COLOR.gold : COLOR.ink });
    const portrait = artSprite(open || result ? `boss-${bossId}` : 'boss-locked', 112);
    portrait.position.set(8 + 56, 66);
    const label = caps(`Уровень ${level.index} · босс`, COLOR.pink, 0.5);
    label.position.set(132, 30);
    const name = new Text({
      text: BOSS_NAMES[bossId],
      style: textStyle({ size: 20, display: true, weight: '900' }),
    });
    name.position.set(132, 48);
    // Длинное имя («Вибрион-Спрут») не вылезает за карточку.
    if (name.width > 280 - 132 - 12) name.scale.set((280 - 132 - 12) / name.width);
    const status = new Text({
      text: result ? `Побеждён · ${formatScore(result.bestScore)}` : open ? 'Не пройден' : 'Закрыт',
      style: textStyle({ size: 13, color: 0xe2b8cc }),
    });
    status.position.set(132, 80);
    node.addChild(glow, bg, portrait, label, name, status);
    if (open) {
      this.stops.push(idle(portrait, 'boss'));
      if (!motionReduced()) {
        this.stops.push(idle(glow, 'talk'));
      }
    }
    this.interactive(
      node,
      level,
      open,
      `Уровень ${level.index}, бой с боссом ${BOSS_NAMES[bossId]}`,
    );
    return node;
  }

  private interactive(node: Container, level: LevelView, open: boolean, title: string): void {
    node.eventMode = 'static';
    node.cursor = open ? 'pointer' : 'not-allowed';
    node.accessible = true;
    node.accessibleType = 'button';
    node.accessibleTitle = title;
    node.on('pointertap', () => {
      if (!open) {
        this.notice.text = 'Уровень закрыт: сначала пройдите предыдущий.';
        return;
      }
      this.notice.text = '';
      if (level.id === this.level.id) {
        this.start();
        return;
      }
      this.level = level;
      this.render();
    });
  }

  private renderDetails(): void {
    const level = this.level;
    const open = this.ctx.progress.unlocked.includes(level.id);
    const result = this.ctx.progress.levels[level.id];
    const bossId = level.bossId;
    const tall = this.tall;
    const panelW = tall ? TALL_W : WIDE_W;
    const panelH = tall ? 664 : 270;
    this.details.addChild(panel({ width: panelW, height: panelH, radius: 22, alpha: 0.92 }));
    const colW = tall ? panelW - 52 : 470;

    // Колонка 1: что за уровень.
    const col1 = new Container();
    col1.position.set(26, 22);
    const kicker = caps(
      bossId
        ? `Уровень ${level.index} · бой с боссом`
        : `Глава ${level.chapter} · уровень ${level.index}`,
    );
    const bossScene = level.sceneBefore
      ? this.ctx.content.scenes.find((s) => s.id === level.sceneBefore)
      : undefined;
    const title = new Text({
      text:
        bossId && bossScene
          ? tr(bossScene.title)
          : `${VESSEL_NAMES[level.vessel]} · уровень ${level.index}`,
      style: textStyle({ size: 26, display: true, weight: '900', wrap: colW, lineHeight: 30 }),
    });
    title.position.set(0, 26);
    const bossEntry = bossId
      ? this.ctx.content.catalog.find((e) => e.id === `boss-${bossId}`)
      : undefined;
    const fresh = level.introduces
      .map((id) => this.ctx.content.catalog.find((e) => e.id === id))
      .filter((e) => e !== undefined)
      .map((e) => tr(e.title));
    const description = bossEntry
      ? `${tr(bossEntry.what)} ${tr(bossEntry.how)}`
      : `Очистите ${VESSEL_ACCUSATIVE[level.vessel]} от всех микробов, пока капсулы не дошли до верха.`;
    const text = new Text({
      text: description,
      style: textStyle({ size: 15, color: 0xd6ecf5, wrap: colW, lineHeight: 22 }),
    });
    text.position.set(0, title.y + title.height + 10);
    col1.addChild(kicker, title, text);
    const extra = result
      ? `Рекорд: ${formatScore(result.bestScore)} · лучшая цепочка ×${result.bestChain}`
      : fresh.length > 0
        ? `Новое: ${fresh.join(', ')}`
        : bossScene
          ? `Перед боем — сцена «${tr(bossScene.title)}»`
          : '';
    if (extra) {
      const note = new Container();
      const icon = artSprite(result ? 'icon-check' : 'icon-scene', 20);
      icon.position.set(22, 22);
      const noteText = new Text({
        text: extra,
        style: textStyle({ size: 14, color: COLOR.goldSoft, wrap: colW - 56 }),
      });
      noteText.anchor.set(0, 0.5);
      noteText.position.set(42, 22);
      const box = new Graphics()
        .roundRect(0, 0, colW + 6, 44, 12)
        .fill({ color: COLOR.gold, alpha: 0.1 })
        .stroke({ width: 2, color: COLOR.gold, alpha: 0.5 });
      note.addChild(box, icon, noteText);
      note.position.set(0, tall ? text.y + text.height + 14 : 270 - 22 * 2 - 44);
      col1.addChild(note);
    }

    // Колонка 2: микробы уровня.
    const col2 = new Container();
    // На телефоне колонки идут одна под другой.
    const col1Bottom = 22 + col1.height;
    col2.position.set(tall ? 26 : 26 + 540, tall ? col1Bottom + 18 : 22);
    col2.addChild(caps('Микробы на уровне'));
    const rows: [string, string, number][] = [
      ['microbe-0', 'Вирусы и бактерии трёх видов', level.virusCount],
    ];
    if (level.biofilmCount > 0) rows.push(['armor', 'Из них в биоплёнке', level.biofilmCount]);
    rows.forEach(([key, label, count], i) => {
      const y = 30 + i * 54;
      if (key === 'armor') {
        const icon = artSprite('microbe-2', 32);
        icon.position.set(21, y + 21);
        const armor = artSprite('armor', 42);
        armor.position.set(21, y + 21);
        col2.addChild(icon, armor);
      } else {
        [0, 1, 2].forEach((color) => {
          const icon = artSprite(`microbe-${color}`, 30);
          icon.position.set(15 + color * 22, y + 21);
          col2.addChild(icon);
        });
      }
      const name = new Text({ text: label, style: textStyle({ size: 15, weight: '500' }) });
      name.anchor.set(0, 0.5);
      name.position.set(key === 'armor' ? 54 : 80, y + 21);
      const amount = new Text({
        text: `×${count}`,
        style: textStyle({ size: 15, display: true, weight: '800' }),
      });
      amount.anchor.set(1, 0.5);
      amount.position.set(tall ? colW : 340, y + 21);
      col2.addChild(name, amount);
    });
    const speed = new Text({
      text: `Скорость: капсула падает раз в ${(level.gravityMs / 1000).toFixed(2).replace('.', ',')} с`,
      style: textStyle({ size: 13, color: COLOR.textMuted }),
    });
    speed.position.set(0, 30 + rows.length * 54 + 6);
    col2.addChild(speed);

    // Колонка 3: способность и старт.
    const col3 = new Container();
    const buttonW = tall ? colW : 360;
    col3.position.set(tall ? 26 : 1320 - 26 - 360, tall ? panelH - 22 - 64 - 16 - 72 - 26 : 22);
    col3.addChild(caps('Способность'));
    const offered = level.abilities;
    const ability: SoloAbility | null = offered.includes(loadout.solo)
      ? loadout.solo
      : (offered[0] ?? null);
    const choose = new UiButton({
      width: buttonW,
      height: 72,
      label: ability ? ABILITY_NAMES[ability] : 'Без способности',
      sub: !ability
        ? 'Появится на следующих уровнях'
        : offered.length > 1
          ? `${SOLO_ABILITY_HINTS[ability]} · сменить`
          : SOLO_ABILITY_HINTS[ability],
      icon: ability
        ? { key: `icon-ability-${ability}`, tile: 0x11284a }
        : { key: 'icon-lock', tile: 0x26303f },
      onTap: () => {
        if (offered.length < 2) return;
        const nextAbility = offered[(offered.indexOf(loadout.solo) + 1) % offered.length];
        if (nextAbility) loadout.solo = nextAbility;
        this.render();
      },
    });
    choose.position.set(0, 26);
    const start = new UiButton({
      width: buttonW,
      height: tall ? 72 : 64,
      variant: open ? 'pink' : 'secondary',
      label: !open
        ? 'Уровень закрыт'
        : bossId
          ? 'Начать бой'
          : result
            ? 'Сыграть снова'
            : 'Начать уровень',
      fontSize: 20,
      center: true,
      disabled: !open,
      onTap: () => this.start(),
    });
    start.position.set(0, tall ? 26 + 72 + 16 : 270 - 44 - 64);
    col3.addChild(choose, start);
    this.details.addChild(col1, col2, col3);
  }

  private start(): void {
    if (!this.ctx.progress.unlocked.includes(this.level.id)) return;
    this.nav.play(this.level);
  }

  destroy(): void {
    this.ticker.remove(this.onTick);
    this.listeners.splice(0).forEach((off) => off());
    this.stops.splice(0).forEach((stop) => stop());
    this.view.destroy();
  }
}

/** Имя босса в винительном падеже: «Победи Спорницу». */
function bossAccusative(id: BossId): string {
  const forms: Readonly<Record<BossId, string>> = {
    spornitsa: 'Спорницу',
    grippozavr: 'Гриппозавра',
    vibrion: 'Вибриона-Спрута',
    tuberkul: 'Туберкула',
    chimera: 'Химеру',
  };
  return forms[id];
}
